import { inject } from '@angular/core';
import { Router, Routes } from '@angular/router';
import { permissionsGuard } from '../../../core/auth/guards/permissions.guard';
import { agencyOnboardingGuard } from '../../../core/guards/agency-onboarding.guard';
import { agencyReportsGuard } from '../../../core/auth/guards/agency-reports.guard';
import { agencyShopContextGuard } from '../../../core/guards/agency-shop-context.guard';
import { marketingAccessGuard } from '../../../core/auth/guards/marketing-access.guard';
import { vatSettingsAccessGuard } from '../../../core/auth/guards/vat-settings-access.guard';
import { RoutesEnum } from '../../../core/auth/enums/routes.enum';
import { AgencyStateService } from '../../../core/services/agency-state.service';
import { environment } from '../../../../environments/environment';

export const content: Routes = [
  {
    path: 'dashboard',
    canActivate: [agencyOnboardingGuard, permissionsGuard],
    data: {
      module: 'overview',
      redirectModuleUrl: RoutesEnum.OVERVIEW,
      redirectNextModuleUrl: RoutesEnum.SALES_PROFIT
    },
    loadChildren: () =>
      import('../../../features/dashboard/dashboard.component').then(m => m.DASHBOARD_ROUTES)
  },
  {
    path: 'sales-profit',
    canActivate: [agencyOnboardingGuard, permissionsGuard],
    data: {
      module: 'salesAndProfit',
      redirectModuleUrl: RoutesEnum.SALES_PROFIT,
      redirectNextModuleUrl: RoutesEnum.PROFIT_LOSS
    },
    loadChildren: () =>
      import('../../../features/sales-profit/sales-profit.component').then(
        m => m.SALES_PROFIT_ROUTES
      )
  },
  {
    path: 'profit-loss',
    canActivate: [agencyOnboardingGuard, permissionsGuard],
    data: {
      module: 'profitAndLoss',
      redirectModuleUrl: RoutesEnum.PROFIT_LOSS,
      redirectNextModuleUrl: RoutesEnum.INVENTORY
    },
    loadChildren: () =>
      import('../../../features/profit-loss/profit-loss.component').then(m => m.PROFIT_LOSS_ROUTES)
  },
  {
    path: 'inventory',
    canActivate: [agencyOnboardingGuard, permissionsGuard],
    data: {
      module: 'inventory',
      redirectModuleUrl: RoutesEnum.INVENTORY,
      redirectNextModuleUrl: environment.enableWarehouseManagement
        ? RoutesEnum.WAREHOUSE
        : RoutesEnum.MANUAL_ADJUSTMENTS
    },
    loadChildren: () =>
      import('../../../features/inventory/inventory.component').then(m => m.INVENTORY_ROUTES)
  },
  // Conditionally include warehouse route based on feature flag
  ...(environment.enableWarehouseManagement
    ? [
        {
          path: 'warehouse',
          canActivate: [agencyOnboardingGuard, permissionsGuard],
          data: {
            module: 'warehouse',
            redirectModuleUrl: RoutesEnum.WAREHOUSE,
            redirectNextModuleUrl: RoutesEnum.MANUAL_ADJUSTMENTS
          },
          loadChildren: () =>
            import('../../../features/warehouse/warehouse.module').then(m => m.WarehouseModule)
        }
      ]
    : []),
  {
    path: 'partner',
    loadChildren: () =>
      import('../../../features/partner-dashboard/partner-dashboard.component').then(
        m => m.PARTNER_DASHBOARD_ROUTES
      )
  },
  {
    path: 'settings',
    // Захищено authGuard на батьківському рівні (app-routing.module.ts:45)
    // Доступ до окремих вкладок контролюється через ModuleAccessDirective в template
    // Це дозволяє всім залогіненим користувачам бачити settings з відповідними вкладками
    loadChildren: () =>
      import('../../../features/settings/settings.component').then(m => m.SETTINGS_ROUTES)
  },
  {
    path: 'manual-adjustments',
    canActivate: [agencyOnboardingGuard, permissionsGuard],
    data: {
      module: 'manualAdjustments',
      redirectModuleUrl: RoutesEnum.MANUAL_ADJUSTMENTS,
      redirectNextModuleUrl: RoutesEnum.SETTINGS
    },
    loadChildren: () =>
      import('../../../features/manual-adjustments/manual-adjustments.component').then(
        m => m.MANUAL_ADJUSTMENTS_ROUTES
      )
  },
  ...(environment.enableVatSettings
    ? [
        {
          path: 'vat-settings',
          canActivate: [agencyOnboardingGuard, permissionsGuard, vatSettingsAccessGuard],
          data: {
            module: 'profitAndLoss',
            redirectModuleUrl: RoutesEnum.VAT_SETTINGS,
            redirectNextModuleUrl: RoutesEnum.SETTINGS
          },
          loadChildren: () =>
            import('../../../features/vat-settings/vat-settings.component').then(
              m => m.VAT_SETTINGS_ROUTES
            )
        }
      ]
    : []),
  {
    path: 'product/:id',
    canActivate: [agencyOnboardingGuard, permissionsGuard],
    data: {
      module: 'inventory', // Продукт належить до inventory модуля
      redirectModuleUrl: RoutesEnum.INVENTORY,
      redirectNextModuleUrl: RoutesEnum.SETTINGS
    },
    loadChildren: () =>
      import('../../../features/product/product.component').then(m => m.PRODUCTS_ROUTES)
  },
  {
    path: 'customers',
    canActivate: [agencyOnboardingGuard, permissionsGuard],
    data: {
      module: 'customers',
      redirectModuleUrl: RoutesEnum.CUSTOMERS,
      redirectNextModuleUrl: RoutesEnum.SETTINGS
    },
    loadChildren: () =>
      import('../../../features/customers/customers.component').then(m => m.CUSTOMERS_ROUTES)
  },
  {
    path: 'customers/product-journey',
    canActivate: [agencyOnboardingGuard, permissionsGuard],
    data: {
      module: 'customers',
      redirectModuleUrl: RoutesEnum.CUSTOMERS,
      redirectNextModuleUrl: RoutesEnum.SETTINGS
    },
    loadChildren: () =>
      import('../../../features/customers/cohort-journey/cohort-journey-flow.component').then(
        m => m.COHORT_JOURNEY_ROUTES
      )
  },
  {
    path: 'customers/cohort-retention',
    canActivate: [agencyOnboardingGuard, permissionsGuard],
    data: {
      module: 'customers',
      redirectModuleUrl: RoutesEnum.CUSTOMERS,
      redirectNextModuleUrl: RoutesEnum.SETTINGS
    },
    loadChildren: () =>
      import('../../../features/customers/cohort-journey/cohort-retention.component').then(
        m => m.COHORT_RETENTION_ROUTES
      )
  },
  {
    path: 'orders',
    canActivate: [agencyOnboardingGuard, permissionsGuard],
    data: {
      module: 'orders',
      redirectModuleUrl: RoutesEnum.ORDERS,
      // Skip marketing if disabled, go directly to settings
      redirectNextModuleUrl: environment.enableMarketing
        ? RoutesEnum.MARKETING
        : RoutesEnum.SETTINGS
    },
    loadChildren: () =>
      import('../../../features/orders/orders.component').then(m => m.ORDERS_ROUTES)
  },
  {
    path: 'agency-onboarding',
    // Function-form redirectTo (Angular 17+) so we honour the /demo
    // prefix: from /demo/agency-onboarding we land on
    // /demo/agency/clients, not /agency/clients (which would leave the
    // demo subtree and bounce through DemoGuard → authGuard). The
    // RedirectFunction's redirectData only exposes the matched
    // segments, so we pull the in-flight full URL from Router's
    // current Navigation. (bd-1utn)
    redirectTo: () => {
      const router = inject(Router);
      const inFlightUrl = router.getCurrentNavigation()?.extractedUrl?.toString() ?? '';
      return AgencyStateService.demoAwareUrl(inFlightUrl, '/agency/clients');
    },
    pathMatch: 'full'
  },
  // Marketing route - compile-time gated by enableMarketing (master switch),
  // then RUNTIME per-shop gated by marketingAccessGuard (MARKETING_ENABLED_SHOPS).
  ...(environment.enableMarketing
    ? [
        {
          path: 'marketing',
          canActivate: [agencyOnboardingGuard, permissionsGuard, marketingAccessGuard],
          data: {
            module: 'marketing',
            redirectModuleUrl: RoutesEnum.MARKETING,
            redirectNextModuleUrl: RoutesEnum.SETTINGS
          },
          loadChildren: () =>
            import('../../../features/marketing/marketing.component').then(m => m.MARKETING_ROUTES)
        }
      ]
    : []),
  {
    path: 'reactivate',
    loadChildren: () =>
      import('../../../features/reactivate/reactivate.component').then(m => m.REACTIVATE_ROUTES)
  }
];

export const agencyContent: Routes = [
  {
    path: 'dashboard',
    loadComponent: () =>
      import('../../../features/agency-dashboard/agency-dashboard.component').then(
        m => m.AgencyDashboardComponent
      )
  },
  {
    path: 'clients',
    loadComponent: () =>
      import('../../../features/agency-clients/agency-clients.component').then(
        m => m.AgencyClientsComponent
      )
  },
  {
    path: 'reports',
    canActivate: [agencyReportsGuard],
    loadComponent: () =>
      import('../../../features/agency-reports/agency-reports.component').then(
        m => m.AgencyReportsComponent
      )
  },
  {
    path: 'reports/:id',
    canActivate: [agencyReportsGuard],
    loadComponent: () =>
      import(
        '../../../features/agency-reports/components/report-preview/report-preview.component'
      ).then(m => m.ReportPreviewComponent)
  },
  {
    path: 'settings',
    loadChildren: () =>
      import('../../../features/settings/settings.component').then(m => m.SETTINGS_ROUTES)
  },
  // Brand pages inside agency context (bd-8048): same components as the
  // root-level shop routes, but reachable as `/agency/shops/:shopId/*` so the
  // agency sidebar (V2-C rail + drawer) stays visible. The guard reads the
  // `:shopId` URL param and sets it as the active shop in `ShopState` before
  // the brand-page component renders.
  {
    path: 'shops/:shopId',
    canActivate: [agencyShopContextGuard],
    children: content
  },
  {
    path: '',
    redirectTo: 'dashboard',
    pathMatch: 'full'
  }
];
